import type Context from '@/context';
import { CircuitType } from '@/schemas/common';
import { observeSnapshotInput } from '@/services/projectSnapshot/observeSnapshotInput';
import { TileType } from './TileTypes';
import { autorun, untracked } from 'mobx';

type ObservePart = (owner: object, read: () => void) => void;

/** Keep input/deep-state invalidations local to their tile or placed instance.
 * The outer observer owns collection membership; removed parts are detached.
 * Every change still invalidates the save revision synchronously. */
export function trackProjectAuthoring(context: Context, changed: () => void): () => void {
  const parts = new Map<object, () => void>();
  let initial = true;
  const dispose = autorun(() => {
    const retained = new Set<object>();
    observeProjectAuthoring(context, (owner, read) => {
      retained.add(owner);
      if (parts.has(owner)) return;
      let first = true;
      const stop = autorun(() => {
        read();
        if (first) first = false;
        else untracked(changed);
      });
      parts.set(owner, stop);
    });
    for (const [owner, stop] of parts) {
      if (retained.has(owner)) continue;
      stop();
      parts.delete(owner);
    }
    if (initial) initial = false;
    else untracked(changed);
  });
  return () => {
    dispose();
    for (const stop of parts.values()) stop();
    parts.clear();
  };
}

/** Observe document edits and input intent, without subscribing to simulation frames.
 * Runtime snapshots still capture a consistent point in time, but scheduler progress
 * alone neither dirties the document nor triggers another storage write. */
export function observeProjectAuthoring(
  context: Context,
  observePart: ObservePart = (_owner, read) => read(),
): void {
  void context.project.name;
  void context.mainChip.id;
  for (const chip of context.chipStore.chips) {
    void chip.id;
    void chip.kind;
    void chip.templateType;
    observeSnapshotInput(chip.templateOrigin);
    void chip.name;
    void chip.description;
    void chip.folder;
    void chip.color;
    void chip.combinedColorDisplay;
    observeSnapshotInput(chip.adapterSignals);
    void chip.structureCircuit.originLocation.x;
    void chip.structureCircuit.originLocation.y;
    for (const circuit of [
      chip.logicCircuit.circuitModel,
      chip.structureCircuit.circuitModel,
    ]) {
      void circuit.id;
      void circuit.circuitType;
      void circuit.connectionModel.topologyVersion;
      observeSnapshotInput(circuit.labels?.tags);
      observeSnapshotInput(circuit.labels?.sections);
      for (const tile of circuit.tiles) {
        observePart(tile, () => {
          void tile.id;
          void tile.tileType;
          void tile.globalTilePosition.chipInstanceId;
          void tile.globalTilePosition.circuitType;
          void tile.globalTilePosition.location.x;
          void tile.globalTilePosition.location.y;
          // Inputs/outputs are frame projections. Other metadata describes authoring.
          for (const key of Object.keys(tile.meta))
            if (key !== 'inputs' && key !== 'outputs')
              observeSnapshotInput(
                (tile.meta as unknown as Record<string, unknown>)[key],
              );
          const state = tile.state as Record<string, unknown> | null;
          switch (tile.tileType) {
            case TileType.Clock:
              void state?.intervalMs;
              break;
            case TileType.Delay:
              void state?.delayTimeMs;
              break;
            case TileType.Random:
              break;
            default:
              // Extension runtime fields are opaque. Their authored changes are
              // observed through editor transactions instead of arbitrary tick data.
              if (!tile.tileType.startsWith('extension:')) observeSnapshotInput(state);
          }
        });
      }
    }
    for (const link of chip.interfaceLinks) {
      void link.tiles[CircuitType.ChipLogic]?.id;
      void link.tiles[CircuitType.ChipStructure]?.id;
      for (const key of Object.keys(link))
        if (key !== 'tiles')
          observeSnapshotInput((link as unknown as Record<string, unknown>)[key]);
    }
    for (const instance of chip.logicCircuit.chipInstances.values())
      observePart(instance, () => instance.trackSnapshotDependencies?.());
    const editingInstance = chip.editingInstance;
    observePart(editingInstance, () => editingInstance.trackSnapshotDependencies?.());
    const editor = context.chipStore.getChipEditor(chip.id);
    for (const circuitEditor of [
      editor?.logicCircuitEditor,
      editor?.structureCircuitEditor,
    ]) {
      const history = circuitEditor?.historyModel;
      if (!history) continue;
      void history.previousTransactions.length;
      void history.futureTransactions.length;
      void history.currentTransaction?.actions.length;
    }
  }
}
